import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { PawPrint, ArrowRight, ArrowLeft, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const US_CITIES = [
  "Charlotte", "Los Angeles", "New York", "Atlanta", "Dallas", "Houston",
  "Miami", "Chicago", "Boston", "Washington DC", "San Francisco", "Seattle",
  "Denver", "Austin", "Nashville", "Philadelphia", "San Diego", "Las Vegas",
  "Portland", "Tampa", "Orlando",
];
const MX_CITIES = ["Monterrey", "CDMX", "Guadalajara", "Cancun", "Tijuana", "Querétaro", "Puebla", "Mérida", "León"];
const SERVICES = ["one_on_one", "board_train", "group", "train_trainer", "behavioral", "puppy", "reactivity"];

export default function RequestTraining() {
  const { t, lang } = useI18n();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    family_size: 2,
    has_kids: false,
    dog_name: "",
    dog_breed: "",
    dog_age: "",
    dog_temperament: "",
    service: "one_on_one",
    goals: "",
    country: "US",
    city: "",
    preferred_schedule: "",
    notes: "",
    language: lang,
  });
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const totalSteps = 5;
  const progress = ((step - 1) / totalSteps) * 100;

  const next = () => setStep((s) => Math.min(s + 1, totalSteps));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads", form);
      setDone(data);
      toast.success(t("form.success"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const cities = form.country === "US" ? US_CITIES : MX_CITIES;

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <Card className="rounded-3xl border-border/60 soft-shadow" data-testid="lead-success-card">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/15 mx-auto flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl font-black tracking-tight mb-3">{t("form.success")}</h2>
            <p className="text-muted-foreground mb-8">
              {lang === "es"
                ? "Te emparejamos con estos entrenadores. Pronto te contactaremos para confirmar."
                : "We've matched you with these trainers. We'll reach out shortly to confirm."}
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {(done.suggested_trainers || []).slice(0, 3).map((tr) => (
                <div key={tr.id} className="rounded-2xl bg-card border border-border/60 p-4 text-left soft-shadow">
                  <img src={tr.avatar} alt={tr.name} className="w-14 h-14 rounded-2xl object-cover mb-2" />
                  <p className="font-bold">{tr.name}</p>
                  <p className="text-xs text-muted-foreground">{tr.city}, {tr.country}</p>
                  <p className="text-xs text-primary mt-1 font-bold">★ {tr.rating}</p>
                </div>
              ))}
            </div>
            {done.lead?.needs_traveling_trainer && (
              <div className="rounded-2xl bg-accent/30 border border-border/60 p-4 text-sm font-semibold flex items-center gap-2 justify-center">
                <Sparkles className="w-4 h-4" />
                {lang === "es"
                  ? "No tenemos a alguien local — ¡llevaremos un entrenador a ti!"
                  : "No local trainer yet — we'll bring one to you!"}
              </div>
            )}
            <Button
              className="mt-8 rounded-full"
              onClick={() => {
                setDone(null);
                setStep(1);
              }}
            >
              {lang === "es" ? "Otra solicitud" : "Submit another"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-8">
        <PawPrint className="w-10 h-10 text-primary mx-auto mb-3 wiggle" />
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2">{t("form.title")}</h1>
        <p className="text-muted-foreground">{t("form.sub")}</p>
      </div>

      <Card className="rounded-3xl border-border/60 soft-shadow">
        <CardContent className="p-6 sm:p-10">
          <div className="flex items-center justify-between mb-6">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              {t("form.step")} {step} {t("form.of")} {totalSteps}
            </span>
            <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="mb-8 h-2" />

          {step === 1 && (
            <div className="space-y-4" data-testid="form-step-1">
              <h2 className="font-display text-2xl font-bold mb-3">{t("form.s1.title")}</h2>
              <div>
                <Label>{t("form.f.parent_name")}</Label>
                <Input value={form.parent_name} onChange={(e) => setF("parent_name", e.target.value)} className="mt-1.5 rounded-xl" data-testid="form-parent-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("form.f.parent_email")}</Label>
                  <Input type="email" value={form.parent_email} onChange={(e) => setF("parent_email", e.target.value)} className="mt-1.5 rounded-xl" data-testid="form-parent-email" />
                </div>
                <div>
                  <Label>{t("form.f.parent_phone")}</Label>
                  <Input value={form.parent_phone} onChange={(e) => setF("parent_phone", e.target.value)} className="mt-1.5 rounded-xl" data-testid="form-parent-phone" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("form.f.family_size")}</Label>
                  <Input type="number" min={1} value={form.family_size} onChange={(e) => setF("family_size", parseInt(e.target.value || 1))} className="mt-1.5 rounded-xl" />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border bg-muted/30 px-4 mt-7">
                  <Label className="font-semibold">{t("form.f.has_kids")}</Label>
                  <Switch checked={form.has_kids} onCheckedChange={(v) => setF("has_kids", v)} data-testid="form-has-kids" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="form-step-2">
              <h2 className="font-display text-2xl font-bold mb-3">{t("form.s2.title")}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("form.f.dog_name")}</Label>
                  <Input value={form.dog_name} onChange={(e) => setF("dog_name", e.target.value)} className="mt-1.5 rounded-xl" data-testid="form-dog-name" />
                </div>
                <div>
                  <Label>{t("form.f.dog_breed")}</Label>
                  <Input value={form.dog_breed} onChange={(e) => setF("dog_breed", e.target.value)} className="mt-1.5 rounded-xl" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("form.f.dog_age")}</Label>
                  <Input value={form.dog_age} onChange={(e) => setF("dog_age", e.target.value)} className="mt-1.5 rounded-xl" placeholder="e.g. 2 years" />
                </div>
                <div>
                  <Label>{t("form.f.dog_temperament")}</Label>
                  <Select value={form.dog_temperament} onValueChange={(v) => setF("dog_temperament", v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {["playful", "shy", "reactive", "anxious", "calm", "high-energy"].map((x) => (
                        <SelectItem key={x} value={x}>{x}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="form-step-3">
              <h2 className="font-display text-2xl font-bold mb-3">{t("form.s3.title")}</h2>
              <div>
                <Label>{t("form.f.service")}</Label>
                <Select value={form.service} onValueChange={(v) => setF("service", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="form-service"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s}>{t(`service.${s}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("form.f.goals")}</Label>
                <Textarea
                  rows={4}
                  value={form.goals}
                  onChange={(e) => setF("goals", e.target.value)}
                  className="mt-1.5 rounded-xl"
                  placeholder={lang === "es" ? "Cuéntanos qué te gustaría lograr…" : "Tell us what you'd love to achieve…"}
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="form-step-4">
              <h2 className="font-display text-2xl font-bold mb-3">{t("form.s4.title")}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("form.f.country")}</Label>
                  <Select value={form.country} onValueChange={(v) => { setF("country", v); setF("city", ""); }}>
                    <SelectTrigger className="mt-1.5 rounded-xl" data-testid="form-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="US">🇺🇸 United States</SelectItem>
                      <SelectItem value="MX">🇲🇽 México</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("form.f.city")}</Label>
                  <Select value={form.city} onValueChange={(v) => setF("city", v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl" data-testid="form-city"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="other">
                        {lang === "es" ? "Otra — llegamos a ti" : "Other / not listed — we'll come to you"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {lang === "es"
                      ? "✈️ Llegamos a donde estés. Si no ves tu ciudad, elige la más cercana o «Otra»."
                      : "✈️ We meet you wherever you're at. If your city isn't listed, pick the closest or \"Other\"."}
                  </p>
                </div>
              </div>
              <div>
                <Label>{t("form.f.preferred_schedule")}</Label>
                <Input value={form.preferred_schedule} onChange={(e) => setF("preferred_schedule", e.target.value)} className="mt-1.5 rounded-xl" placeholder={lang === "es" ? "ej. fines de semana" : "e.g. weekday evenings"} />
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="form-step-5">
              <h2 className="font-display text-2xl font-bold mb-3">{t("form.s5.title")}</h2>
              <div>
                <Label>{t("form.f.notes")}</Label>
                <Textarea rows={5} value={form.notes} onChange={(e) => setF("notes", e.target.value)} className="mt-1.5 rounded-xl" />
              </div>
              <div className="rounded-2xl bg-secondary/30 border border-border/60 p-4 text-sm">
                <p className="font-bold mb-1">{lang === "es" ? "Resumen" : "Summary"}</p>
                <p className="text-muted-foreground">
                  {form.dog_name || "?"} · {form.dog_breed || "?"} · {t(`service.${form.service}`)} · {form.city || "?"}, {form.country}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 gap-3">
            <Button variant="outline" onClick={back} disabled={step === 1} className="rounded-full" data-testid="form-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {t("form.back")}
            </Button>
            {step < totalSteps ? (
              <Button onClick={next} className="rounded-full" data-testid="form-next-btn">
                {t("form.next")} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={busy} className="rounded-full font-bold" data-testid="form-submit-btn">
                {busy ? "…" : t("form.submit")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
